import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { getAdminToken, adminLogout } from "../lib/adminAuth";
import { Star, Mail, Loader2, Inbox } from "lucide-react";

const STATUSES = ["new", "read", "replied", "archived"];
const STATUS_STYLE = {
  new: "bg-terracotta/20 text-terracotta",
  read: "bg-sage/25 text-ink-soft",
  replied: "bg-gold/20 text-gold-deep",
  archived: "bg-ink/10 text-ink-muted",
};

export default function AdminApplications() {
  const [token] = useState(() => getAdminToken());
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/applications`, auth);
      setRows(data.applications || []);
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not load applications.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const patch = async (id, body) => {
    try {
      await axios.patch(`${API}/admin/applications/${id}`, body, auth);
      setRows((r) => r.map((x) => (x.id === id ? { ...x, ...body } : x)));
      setSel((s) => (s?.id === id ? { ...s, ...body } : s));
    } catch (e) {
      if (e.response?.status === 401) { adminLogout(); return; }
      setErr("Could not update. Please try again.");
    }
  };

  const open = (r) => {
    setSel(r);
    if (r.status === "new") patch(r.id, { status: "read" });
  };

  const unreadCount = rows.filter((r) => r.status === "new").length;

  return (
    <div className="min-h-[100dvh] bg-ivory-100" data-testid="admin-applications">
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <AdminNav onLogout={adminLogout} />
        <h1 className="font-display text-3xl text-ink">Immersion Applications</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Each quiet inventory, sent by someone ready to lay something down.
          {unreadCount > 0 ? ` ${unreadCount} new.` : ""}
        </p>
      </div>

      <div className="mx-auto grid max-w-6xl gap-6 px-5 py-8 sm:grid-cols-[340px_1fr] sm:px-8">
        <div className="space-y-2">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-ink-muted" /></div>}
          {!loading && err && <p className="text-sm text-terracotta" data-testid="applications-error">{err}</p>}
          {!loading && !err && rows.length === 0 && (
            <div className="rounded-xl bg-ivory-50 p-6 text-center ring-1 ring-ivory-400/50">
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gold/10 text-gold-deep"><Inbox className="h-5 w-5" /></span>
              <p className="mt-3 text-sm text-ink-muted">No applications yet — they'll arrive here as readers apply.</p>
            </div>
          )}
          {rows.map((r) => (
            <button
              key={r.id}
              onClick={() => open(r)}
              data-testid={`application-row-${r.id}`}
              className={`block w-full rounded-xl p-3 text-left ring-1 transition ${sel?.id === r.id ? "bg-gold/10 ring-gold/40" : "bg-ivory-50 ring-ivory-400/50 hover:ring-gold/30"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate font-display ${r.status === "new" ? "text-ink" : "text-ink-soft"}`}>
                  {r.starred ? <Star className="mr-1 inline h-3.5 w-3.5 fill-gold text-gold-deep" /> : null}
                  {r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Applicant"}
                </span>
                <span className={`flex-none rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[r.status] || STATUS_STYLE.new}`}>{r.status || "new"}</span>
              </div>
              <p className="truncate text-xs text-ink-muted">{r.email || "—"} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</p>
            </button>
          ))}
        </div>

        <div>
          {!sel ? (
            <p className="text-sm text-ink-muted">Select an application to read it.</p>
          ) : (
            <div className="rounded-2xl bg-ivory-50 p-6 ring-1 ring-ivory-400/50 sm:p-7" data-testid="application-detail">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => patch(sel.id, { starred: !sel.starred })}
                  data-testid="application-star-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${sel.starred ? "bg-gold/20 text-gold-deep" : "border border-ink/20 text-ink-soft"}`}
                >
                  <Star className={`h-3.5 w-3.5 ${sel.starred ? "fill-gold text-gold-deep" : ""}`} /> {sel.starred ? "Starred" : "Star"}
                </button>
                <span className="mx-1 h-4 w-px bg-ink/10" />
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => patch(sel.id, { status: s })}
                    data-testid={`application-set-status-${s}`}
                    className={`rounded-full px-3 py-1 text-xs capitalize ${sel.status === s ? "bg-ink text-ivory-50" : "border border-ink/20 text-ink-soft"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <p className="mt-5 font-display text-xl text-ink">{sel.full_name || `${sel.first_name || ""} ${sel.last_name || ""}`.trim() || "Applicant"}</p>
              <p className="text-sm text-ink-muted">
                {sel.email || "—"}{sel.phone ? ` · ${sel.phone}` : ""}
                {sel.created_at ? ` · ${new Date(sel.created_at).toLocaleString()}` : ""}
              </p>

              {Array.isArray(sel.responses) && sel.responses.length > 0 ? (
                <div className="mt-6 divide-y divide-ivory-400/50">
                  {sel.responses.map((qa, i) => (
                    <div key={i} className="py-4">
                      <p className="text-[13px] leading-snug text-ink-muted">{i + 1}. {qa.question}</p>
                      <p className="mt-1 whitespace-pre-wrap text-[15px] leading-relaxed text-ink">{qa.answer || "—"}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-6 whitespace-pre-wrap text-[15px] leading-relaxed text-ink">{sel.message || "No written answers on file."}</p>
              )}

              {sel.email && (
                <a
                  href={`mailto:${sel.email}?subject=${encodeURIComponent("About your Immersion application")}`}
                  onClick={() => patch(sel.id, { status: "replied" })}
                  data-testid="application-reply"
                  className="debossed-btn mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-ink"
                >
                  <Mail className="h-4 w-4" /> Reply personally
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
